import { Dialog, Transition } from '@headlessui/react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { type Swiper as SwiperType } from 'swiper'
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react'

import { useStoriesSteps } from '@/api/models/stories/hooks/useStoriesSteps'
import { useViewStory } from '@/api/models/stories/hooks/useViewStory'
import { Story } from '@/api/models/stories/lib/stories.types'

import { ErrorSlide } from '../ErrorSlide'
import { LoadingSlide } from './LoadingSlide'
import { ProgressBar } from './SliderProgressBar'
import { StorySlide } from './StorySlide'

type Props = {
  stories: Story[]
  onClose: () => void
  onAfterLeave?: () => void
  opened: boolean
}

const AUTOPLAY_DELAY = 15_000

const StoriesDrawer = ({ onClose, onAfterLeave, opened, stories }: Props) => {
  const searchParams = useSearchParams()
  const swiperRef = useRef<SwiperType | null>(null)
  const storyId = searchParams?.get('storyId') ?? ''
  const pathname = usePathname()

  const [currentSliderIndex, setCurrentSliderIndex] = useState(0)

  const router = useRouter()

  const {
    data: steps,
    isFetching,
    isError: isStoriesStepsError
  } = useStoriesSteps(storyId)
  const { mutate: setStoryViewed, isError: isViewStoryError } = useViewStory()

  const isError = isStoriesStepsError || isViewStoryError

  useEffect(() => {
    setCurrentSliderIndex(0)
    swiperRef.current?.slideTo(0, 0)

    if (!opened || isFetching || isError || !steps?.length) {
      return
    }

    const firstSlideId = steps[0]?.id
    if (firstSlideId) {
      setStoryViewed(firstSlideId)
    }
  }, [isError, isFetching, opened, setStoryViewed, steps, storyId])

  const navigateToStory = useCallback(
    (storyId: string) => {
      setCurrentSliderIndex(0)

      const params = new URLSearchParams(searchParams?.toString())
      params.set('storyId', storyId)
      router.push(`${pathname}?${params}`)
    },
    [pathname, router, searchParams]
  )

  const currentIndex = stories.findIndex(story => story.id === storyId)
  const nextStory = currentIndex + 1
  const prevStory = currentIndex - 1

  const navigateToPrevStory = () => {
    if (prevStory < 0) {
      return
    }
    navigateToStory(stories[prevStory].id)
  }

  const navigateToNextStory = useCallback(() => {
    if (!stories[nextStory]?.id) {
      onClose()
      return
    }
    navigateToStory(stories[nextStory].id)
  }, [nextStory, onClose, stories, navigateToStory])

  const moveToNextStep = useCallback(() => {
    const isLastStep =
      !!steps?.length && currentSliderIndex === steps.length - 1

    if (isLastStep) {
      navigateToNextStory()
      return
    }

    swiperRef.current?.slideNext()
  }, [currentSliderIndex, navigateToNextStory, steps])

  const onSlideChange = (swiper: SwiperType) => {
    const currentStepIndex = swiper.activeIndex

    if (steps?.length) {
      const currentSlideId = steps[currentStepIndex]?.id
      setCurrentSliderIndex(currentStepIndex)

      if (currentSlideId) {
        setStoryViewed(currentSlideId)
      }
    }
  }

  useEffect(() => {
    if (!opened || isFetching || isError || !steps?.length) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      moveToNextStep()
    }, AUTOPLAY_DELAY)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    currentSliderIndex,
    isError,
    isFetching,
    moveToNextStep,
    opened,
    steps,
    storyId
  ])

  const onSlideClick = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index, 100)
    }
  }

  const isFirstStep =
    (swiperRef.current?.activeIndex ?? 0) === 0 &&
    stories.findIndex(story => story.id === storyId) === 0

  const isSingleStoryWithSingleStep =
    stories.length === 1 && (steps?.length ?? 0) === 1

  if (!storyId) {
    return
  }

  return (
    <Transition appear show={opened} as={Fragment} afterLeave={onAfterLeave}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          enter="duration-75 xs:duration-300"
          enterFrom="opacity-0"
          leave="duration-75 delay-300 xs:duration-300 xs:delay-0"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 xxs:bg-transparent" />
        </Transition.Child>

        <div className="fixed inset-0 bottom-0 z-50 flex h-full max-h-full justify-end sm:top-0">
          <Transition.Child
            as={Fragment}
            enter="duration-300 delay-100 xs:delay-0"
            enterFrom="translate-x-[100%] xs:translate-x-0 xs:translate-y-16 xs:opacity-0"
            enterTo="translate-x-0 xs:translate-y-0"
            leave="duration-300"
            leaveFrom="translate-x-0 xs:translate-x-0 xs:translate-y-0"
            leaveTo="translate-x-[100%] xs:translate-x-0 xs:translate-y-16 xs:opacity-0"
          >
            <Dialog.Panel className="relative flex h-full w-full max-w-[480px] flex-col overflow-hidden rounded-l-20 sm:rounded-0 xxs:w-full">
              {isFetching && <LoadingSlide onClose={onClose} />}
              {isError && <ErrorSlide onClose={onClose} />}
              {!isFetching && !isError && (
                <SwiperComponent
                  slidesPerView={1}
                  spaceBetween={0}
                  speed={0}
                  updateOnWindowResize={true}
                  watchOverflow
                  threshold={10}
                  className="h-full w-full"
                  grabCursor
                  onSwiper={(swiper: SwiperType) =>
                    (swiperRef.current = swiper)
                  }
                  onSlideChange={onSlideChange}
                >
                  <ProgressBar
                    activeIndex={currentSliderIndex || 0}
                    slidesLength={steps?.length || 1}
                    onSlideClick={index => onSlideClick(index)}
                    onClose={onClose}
                  />
                  {steps?.map((step, index) => (
                    <SwiperSlide key={storyId + step.id}>
                      <StorySlide
                        hideRightArrow={isSingleStoryWithSingleStep}
                        hideLeftArrow={isFirstStep}
                        step={step}
                        index={index}
                        swiperRef={swiperRef}
                        stepsLength={steps.length || 1}
                        navigateToPrevStory={navigateToPrevStory}
                        navigateToNextStory={navigateToNextStory}
                      />
                    </SwiperSlide>
                  ))}
                </SwiperComponent>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}

export default StoriesDrawer
